/// <reference types="vite/client" />
// API Client for MegaJobNepal Backend

import { safeStorage } from './safe-storage';

function resolveBaseURL(): string {
  if (typeof window === 'undefined') return '/api';

  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('192.168.') || hostname.includes('10.0.');

  const envUrl = import.meta.env?.VITE_API_URL as string | undefined;

  // 1. If we are running locally, use the relative /api path.
  // This triggers Vite's dev server proxy (configured in vite.config.ts)
  // which routes /api to http://localhost:3005 and avoids all CORS issues.
  if (isLocal) {
    return '/api';
  }

  // 2.In Production (megajobnepal.com), use the provided absolute URL.
  // Since the backend is on a different subdomain (api.megajobnepal.com),
  // we MUST use the full URL.
  if (envUrl && typeof envUrl === 'string' && envUrl.trim().length > 0) {
    const value = envUrl.trim();
    if (/^https?:\/\//i.test(value)) {
      return value.replace(/\/+$/, '');
    }
    return value.startsWith('/') ? value : `/${value}`;
  }

  // 3. Fallback
  return 'https://api.megajobnepal.com';
}

const API_BASE_URL = resolveBaseURL();

console.log('API Client Debug - VITE_API_URL:', import.meta.env.VITE_API_URL);
console.log('API Client Debug - Resolved API_BASE_URL:', API_BASE_URL);

class APIClient {
  private baseURL: string;
  private token: string | null = null;



  constructor(baseURL: string = API_BASE_URL) {
    console.log(baseURL)
    this.baseURL = baseURL;
    this.loadTokenFromStorage();
  }

  // Token discovery across common keys and storages
  private getAnyTokenFromStorage(): string | null {
    try {
      const keys = [
        'megajobnepal_auth_token',
        'auth_token',
        'access_token',
        'token'
      ];
      for (const k of keys) {
        const v = safeStorage.getItem(k);
        if (v) return v;
      }
      // Fallback direct access if safeStorage is constrained
      if (typeof window !== 'undefined') {
        for (const k of keys) {
          const v1 = window.localStorage?.getItem(k);
          if (v1) return v1;
          const v2 = window.sessionStorage?.getItem(k);
          if (v2) return v2;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  private loadTokenFromStorage() {
    try {
      const token = this.getAnyTokenFromStorage();
      if (token) {
        this.token = token;
      }
    } catch (error) {
      console.warn('Could not load token from storage:', error);
    }
  }

  private saveTokenToStorage(token: string) {
    try {
      safeStorage.setItem('megajobnepal_auth_token', token);
      this.token = token;
    } catch (error) {
      console.warn('Could not save token to storage:', error);
    }
  }

  private removeTokenFromStorage() {
    try {
      safeStorage.removeItem('megajobnepal_auth_token');
      this.token = null;
    } catch (error) {
      console.warn('Could not remove token from storage:', error);
    }
  }

  public async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${this.baseURL}${endpoint}`;

    // Refresh token from storage to ensure latest value after login
    try {
      const latestToken = this.getAnyTokenFromStorage();
      if (latestToken && latestToken !== this.token) {
        this.token = latestToken;
      }
    } catch { }

    const defaultHeaders: HeadersInit = {};

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    if (this.token) {
      defaultHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        let errorData = {};
        try { errorData = JSON.parse(text); } catch { }

        const message = (errorData as any).error || (errorData as any).message || `HTTP ${response.status}: ${text.slice(0, 100)}`;
        console.error(`❌ API Error [${response.status}] ${url}:`, { message, text });

        const err: any = new Error(message);
        err.status = response.status;
        err.code = response.status;
        err.data = errorData;
        err.rawText = text;

        if ((err.status === 401 || err.status === 403) && /invalid|expired token/i.test(String(message))) {
          try { this.removeTokenFromStorage(); } catch { }
        }
        throw err;
      }

      if (response.status === 204) {
        return { ok: true };
      }

      const rawText = await response.text();
      try {
        // Always try to parse JSON regardless of Content-Type header
        return JSON.parse(rawText);
      } catch {
        // Fallback to text if it's not JSON
        return rawText;
      }
    } catch (error) {
      const status = (error as any)?.status;
      if (typeof status === 'number' && status < 500) {
        console.warn(`⚠️ API Request Warning: ${url}`, error);
      } else {
        console.error(`❌ API Request Failed: ${url}`, error);
      }

      if (error instanceof Error) {
        const msg = error.message;
        if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('TypeError')) {
          const origin = typeof window !== 'undefined' ? window.location.origin : 'unknown origin';
          const netErr: any = new Error(
            `Network error: Cannot reach ${url}. This is likely a CORS issue or the backend is down. Origin: ${origin}`
          );
          netErr.status = 0;
          throw netErr;
        }
      }
      throw error;
    }
  }

  async requestBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    try {
      const latestToken = this.getAnyTokenFromStorage();
      if (latestToken && latestToken !== this.token) {
        this.token = latestToken;
      }
    } catch { }

    const defaultHeaders: HeadersInit = {};
    if (this.token) {
      defaultHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: { ...defaultHeaders, ...options.headers },
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        const err: any = new Error(message);
        err.status = response.status;
        err.data = errorData;
        throw err;
      }
      return await response.blob();
    } catch (error) {
      if (error instanceof Error) {
        const message = error.message;
        if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('TypeError')) {
          const origin = typeof window !== 'undefined' ? window.location.origin : 'unknown origin';
          const netErr: any = new Error(
            `Network error (Blob): could not reach ${url}. Ensure backend is running and CORS allows ${origin}.`
          );
          netErr.status = 0;
          throw netErr;
        }
      }
      throw error;
    }
  }

  async login(credentials: { email: string; password: string; captchaToken?: string }) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.token) {
      this.saveTokenToStorage(response.token);
    }

    return response;
  }

  async adminLogin(credentials: { email: string; password: string; captchaToken?: string }) {
    const response = await this.request('/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    if (response.token) {
      this.saveTokenToStorage(response.token);
    }
    return response;
  }

  async forgotPassword(email: string) {
    return await this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  logout() {
    this.removeTokenFromStorage();
  }

  async sendOTP(email: string) {
    const response = await this.request('/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return response;
  }

  async verifyOTP(email: string, enteredOtp: string) {
    const response = await this.request('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp: enteredOtp }),
    });
    return response;
  }

  async completeSignup(userData: {
    email: string;
    password: string;
    user_type: string;
    full_name: string;
    otp: string;
    phone_number?: string;
    company_data?: {
      name?: string;
      company_name?: string;
      company_description?: string;
      company_website?: string;
      company_location?: string;
      company_industry?: string;
      company_size?: string;
      company_founded_year?: number;
      description?: string;
      website?: string;
      location?: string;
      industry?: string;
      size?: string;
      founded_year?: number;
      employer_job_title?: string;
      employer_department?: string;
    };
  }) {
    const response = await this.request('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (response.token) {
      this.saveTokenToStorage(response.token);
    }
    return response;
  }

  async getProfile() {
    return await this.request('/users/profile');
  }

  async updateProfile(profileData: any) {
    return await this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  subscribeRealtime(topics: string[] = []) {
    const params = topics.length ? `?topics=${encodeURIComponent(topics.join(','))}` : '';
    const url = `${this.baseURL}/realtime/stream${params}`;
    try {
      const es = new EventSource(url);
      es.onerror = () => { };
      return es;
    } catch (err) {
      console.warn('Realtime subscription failed:', err);
      return null;
    }
  }

  subscribeRealtimePersistent(
    topics: string[] = [],
    onMessage: (evt: MessageEvent) => void,
    opts: { maxRetries?: number; initialDelayMs?: number; maxDelayMs?: number } = {}
  ) {
    const params = topics.length ? `?topics=${encodeURIComponent(topics.join(','))}` : '';
    const url = `${this.baseURL}/realtime/stream${params}`;
    let stopped = false;
    let es: EventSource | null = null;
    let attempts = 0;
    const maxRetries = opts.maxRetries ?? 10;
    const initialDelay = opts.initialDelayMs ?? 1000;
    const maxDelay = opts.maxDelayMs ?? 30000;

    const open = () => {
      if (stopped) return;
      try {
        es = new EventSource(url);
        es.addEventListener('message', onMessage as any);
        es.onerror = () => {
          attempts += 1;
          try { es && es.close(); } catch { }
          const delay = Math.min(initialDelay * Math.pow(2, attempts - 1), maxDelay);
          if (attempts > maxRetries) return;
          setTimeout(open, delay);
        };
      } catch {
        attempts += 1;
        const delay = Math.min(initialDelay * Math.pow(2, attempts - 1), maxDelay);
        if (attempts > maxRetries) return;
        setTimeout(open, delay);
      }
    };

    open();

    return { stop: () => { stopped = true; try { es && es.close(); } catch { } } };
  }

  async getJobs(params: {
    page?: number;
    limit?: number;
    category?: string;
    location?: string;
    type?: string;
    search?: string;
    status?: string;
  } = {}) {
    const queryParams = new URLSearchParams();
    const page = params.page ?? 1;
    const limit = params.limit ?? 10;

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `/jobs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      return await this.request(endpoint);
    } catch (err) {
      console.warn('getJobs failed:', err);
      return {
        jobs: [],
        pagination: {
          current_page: Number(page),
          total_pages: 0,
          total_jobs: 0,
          per_page: Number(limit)
        }
      };
    }
  }

  async getJob(id: string) {
    return await this.request(`/jobs/${id}`);
  }

  async createJob(jobData: any) {
    return await this.request('/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
  }

  // Companies methods
  async getCompanies(params: {
    page?: number;
    limit?: number;
    featured?: boolean;
    topHiring?: boolean;
    search?: string;
    industry?: string;
  } = {}) {
    const queryParams = new URLSearchParams();
    const page = params.page ?? 1;
    const limit = params.limit ?? 10;

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const endpoint = `/companies${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      return await this.request(endpoint);
    } catch (err) {
      console.warn('getCompanies failed:', err);
      return {
        companies: [],
        pagination: {
          current_page: Number(page),
          total_pages: 0,
          total_companies: 0,
          per_page: Number(limit)
        }
      };
    }
  }

  async getTrustedCompanies(limit: number = 20) {
    const qp = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
    return await this.request(`/companies/trusted${qp}`);
  }

  async getCompanyById(id: string) {
    return await this.request(`/companies/${id}`);
  }

  async getCompanyByName(name: string) {
    return await this.request(`/companies/by-name/${encodeURIComponent(name)}`);
  }

  // Applications methods
  async applyForJob(applicationData: {
    jobId: string;
    coverLetter?: string;
    resumeUrl?: string;
  }) {
    return await this.request('/applications', {
      method: 'POST',
      body: JSON.stringify(applicationData),
    });
  }

  async getApplications() {
    return await this.request('/applications');
  }

  // Admin: Get all applications
  async getAdminApplications(params: { page?: number; limit?: number; status?: string; search?: string; jobId?: string; activeJobsOnly?: string } = {}) {
    const queryParams = new URLSearchParams();
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Normalize jobId to backend-compatible job_id
        if (key === 'jobId') {
          queryParams.append('job_id', String(value));
        } else {
          queryParams.append(key, String(value));
        }
      }
    });
    const endpoint = `/admin/applications${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      return await this.request(endpoint);
    } catch (err) {
      console.warn('getAdminApplications failed:', err);
      return {
        applications: [],
        pagination: {
          current_page: Number(page),
          total_pages: 0,
          total_applications: 0,
          per_page: Number(limit)
        }
      };
    }
  }

  // Admin: Update application status
  async updateApplicationStatus(id: string, status: 'pending' | 'reviewed' | 'accepted' | 'rejected') {
    return await this.request(`/admin/applications/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // Admin: Delete application
  async deleteApplication(id: string) {
    return await this.request(`/admin/applications/${id}`, {
      method: 'DELETE',
    });
  }

  // Recruitment methods
  async getRecruitmentItems() {
    return await this.request('/recruitment');
  }

  async createRecruitmentItem(payload: any) {
    return await this.request('/recruitment', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Contact methods
  async sendContactMessage(payload: {
    name: string;
    email: string;
    subject: string;
    category?: string;
    message: string;
    captchaToken?: string;
  }) {
    return await this.request('/contact/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Admin: Contact messages management
  async getContactMessages() {
    return await this.request('/contact/messages', {
      method: 'GET',
    });
  }

  async getContactMessage(id: string) {
    return await this.request(`/contact/messages/${id}`, {
      method: 'GET',
    });
  }

  async updateContactMessageStatus(id: string, status: 'read' | 'replied') {
    return await this.request(`/contact/messages/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteContactMessage(id: string) {
    return await this.request(`/contact/messages/${id}`, {
      method: 'DELETE',
    });
  }

  async updateRecruitmentItem(id: string, payload: any) {
    return await this.request(`/recruitment/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteRecruitmentItem(id: string) {
    // Some backends return 204 with no body; handle gracefully
    try {
      const res = await fetch(`${this.baseURL}/recruitment/${id}`, {
        method: 'DELETE',
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : undefined,
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const message = errorData.error || errorData.message || `HTTP ${res.status}`;
        throw new Error(message);
      }
      return { success: true };
    } catch (err) {
      throw err;
    }
  }

  // Health and status methods
  async getStatus() {
    try {
      const response = await fetch(`${this.baseURL}/status`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Backend status check failed:', error);
      return { status: 'Disconnected', error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  async checkHealth() {
    try {
      // Use the proxied status endpoint to ensure consistent behavior in dev and production
      const response = await fetch(`${this.baseURL}/status`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Backend health check failed:', error);
      return { status: 'Error', error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Check if backend is available
  async isBackendAvailable(): Promise<boolean> {
    try {
      const health = await this.checkHealth();
      return health.status === 'OK' || health.status === 'Connected' || health.status === 'Degraded';
    } catch (error) {
      console.warn('Backend is not available, falling back to localStorage mode');
      return false;
    }
  }

  // File upload methods
  async uploadProfileImage(file: File) {
    const formData = new FormData();
    formData.append('profileImage', file);

    return await this.request('/users/profile/image', {
      method: 'POST',
      body: formData,
    });
  }

  async uploadResume(file: File) {
    const formData = new FormData();
    formData.append('resume', file);

    return await this.request('/users/profile/resume', {
      method: 'POST',
      body: formData,
    });
  }

  // Get current authentication status
  isAuthenticated(): boolean {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      return !!token;
    } catch (error) {
      console.error('Error checking authentication status:', error);
      return false;
    }
  }

  // Upload hero image for homepage section
  async uploadHomepageHeroImage(file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return await this.request('/site/sections/homepage_hero/upload-image', {
      method: 'POST',
      body: formData,
    });
  }

  // Generic: upload image for any site section (e.g., site_info logos)
  async uploadSectionImage(section: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    // Backend route accepts /site/sections/:section/upload-image
    return await this.request(`/site/sections/${section}/upload-image`, {
      method: 'POST',
      body: formData,
    });
  }

  async updateSectionSettings(section: string, settings: any) {
    return await this.request(`/site/sections/${section}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Testimonials API
  async getTestimonials() {
    return await this.request('/testimonials', { method: 'GET' });
  }

  async createTestimonial(payload: { name: string; text: string; role?: string; company?: string; avatar_url?: string; }) {
    return await this.request('/testimonials', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateTestimonial(id: string, payload: { name: string; text: string; role?: string; company?: string; avatar_url?: string; }) {
    return await this.request(`/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteTestimonial(id: string) {
    return await this.request(`/testimonials/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadTestimonialAvatar(id: string, file: File) {
    const formData = new FormData();
    formData.append('image', file);
    return await this.request(`/testimonials/${id}/upload-avatar`, {
      method: 'POST',
      body: formData,
    });
  }

  // Privacy Policy API
  async getPrivacyPolicies() {
    return await this.request('/privacy-policies', { method: 'GET' });
  }

  async createPrivacyPolicy(payload: { title: string; content: string; }) {
    return await this.request('/privacy-policies', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updatePrivacyPolicy(id: string, payload: { title: string; content: string; }) {
    return await this.request(`/privacy-policies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deletePrivacyPolicy(id: string) {
    return await this.request(`/privacy-policies/${id}`, {
      method: 'DELETE',
    });
  }

  // User Interactions
  async getSavedJobs() {
    return await this.request('/user-interactions/saved-jobs');
  }

  async toggleSavedJob(jobId: string) {
    return await this.request(`/user-interactions/saved-jobs/${jobId}`, {
      method: 'POST'
    });
  }

  async getFollowedCompanies() {
    return await this.request('/user-interactions/followed-companies');
  }

  async toggleFollowedCompany(companyId: string) {
    return await this.request(`/user-interactions/followed-companies/${companyId}`, {
      method: 'POST'
    });
  }

  async getBookmarkedNews() {
    return await this.request('/user-interactions/bookmarked-news');
  }

  async toggleBookmarkedNews(newsId: string) {
    return await this.request(`/user-interactions/bookmarked-news/${newsId}`, {
      method: 'POST'
    });
  }

  async uploadTeamMemberImage(file: File, memberId?: string) {
    const formData = new FormData();
    formData.append('image', file);
    const qp = memberId ? `?id=${encodeURIComponent(memberId)}` : '';
    return await this.request(`/site-info/team-members/upload-image${qp}`, {
      method: 'POST',
      body: formData,
    });
  }

  // FAQs API
  async getFaqs() {
    return await this.request('/faqs', { method: 'GET' });
  }

  async createFaq(payload: { question: string; answer: string; category?: string; order?: number }) {
    return await this.request('/faqs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateFaq(id: string, payload: { question?: string; answer?: string; category?: string; order?: number }) {
    return await this.request(`/faqs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteFaq(id: string) {
    return await this.request(`/faqs/${id}`, {
      method: 'DELETE',
    });
  }

  async bulkUpdateFaqs(faqs: any[]) {
    return await this.request('/faqs/bulk', {
      method: 'PUT',
      body: JSON.stringify({ faqs }),
    });
  }

  getToken(): string | null {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      return token || this.token;
    } catch (error) {
      return this.token;
    }
  }

  async sendPasswordChangeOtp(email: string) {
    return await this.request('/auth/send-password-change-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Admin: send password OTP to email (used in some auth UIs)
  async adminSendPasswordOTP(email: string) {
    return await this.request('/admin/password/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async changePasswordWithOtp(email: string, otp: string, newPassword: string) {
    return await this.request('/auth/change-password-with-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp, newPassword }),
    });
  }

  // Admin: change own password without OTP (requires Authorization token)
  async adminChangePassword(payload: { currentPassword: string; newPassword: string }) {
    return await this.request('/admin/password/change', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getAdminUsers(params: { page?: number; limit?: number; q?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    // Use dedicated staff-only endpoint
    return await this.request(`/admin/admin-users${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
  }

  // Get all users (system-wide) for Super Admin overview
  async getAllUsers(params: { page?: number; limit?: number; search?: string; type?: string; status?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/admin/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const res = await this.request(endpoint);
    return Array.isArray((res as any)?.users) ? (res as any) : { users: Array.isArray(res) ? (res as any) : [], pagination: (res as any)?.pagination };
  }

  async createAdminUser(payload: { email: string; password: string; full_name?: string; role?: 'admin' | 'hr' | 'super_admin' }) {
    // Super admin creates staff accounts
    return await this.request('/admin/admin-users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateAdminUser(id: string, payload: { email?: string; name?: string }) {
    // Support PATCH for compatibility
    return await this.request(`/admin/admin-users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteAdminUser(id: string) {
    return await this.request(`/admin/admin-users/${id}`, { method: 'DELETE' });
  }

  async updateUserStatus(id: string, status: 'active' | 'inactive' | 'suspended') {
    return await this.request(`/admin/users/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async updateUserRole(id: string, role: 'admin' | 'hr' | 'super_admin') {
    return await this.request(`/admin/users/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  // Permissions catalog and user permissions management
  async getPermissionsCatalog() {
    return await this.request('/admin/permissions', { method: 'GET' });
  }

  async getUserPermissions(id: string) {
    return await this.request(`/admin/admin-users/${id}/permissions`, { method: 'GET' });
  }

  async updateUserPermissions(id: string, permissions: string[]) {
    return await this.request(`/admin/admin-users/${id}/permissions`, {
      method: 'PATCH',
      body: JSON.stringify({ permissions }),
    });
  }

  async resetUserPassword(id: string, newPassword: string) {
    return await this.request(`/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  }

  async updateUserByAdmin(id: string, payload: any) {
    return await this.request(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async moderateJob(id: string, action: 'approve' | 'reject', reason?: string) {
    return await this.request(`/admin/jobs/${id}/moderate`, {
      method: 'PUT',
      body: JSON.stringify({ action, reason }),
    });
  }

  async createJobByAdmin(payload: any) {
    return await this.request('/admin/jobs', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateJobByAdmin(id: string, payload: any) {
    return await this.request(`/admin/jobs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteJobByAdmin(id: string) {
    return await this.request(`/admin/jobs/${id}`, { method: 'DELETE' });
  }

  async getJobParametersList(type: string) {
    const safeType = String(type || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    return await this.request(`/job-parameters/${safeType}`);
  }

  async createJobParameter(type: string, payload: any) {
    const safeType = String(type || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    return await this.request(`/job-parameters/${safeType}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateJobParameter(type: string, id: string, payload: any) {
    const safeType = String(type || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    return await this.request(`/job-parameters/${safeType}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteJobParameter(type: string, id: string) {
    const safeType = String(type || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    return await this.request(`/job-parameters/${safeType}/${id}`, { method: 'DELETE' });
  }

  // Site info (About + Team)
  async getAboutInfo() {
    return await this.request('/site-info/about');
  }

  async updateAboutInfo(payload: any) {
    return await this.request('/site-info/about', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getTeamMembers(params: { limit?: number } = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined && params.limit !== null) {
      queryParams.append('limit', String(params.limit));
    }
    const endpoint = `/site-info/team-members${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await this.request(endpoint);
  }

  async saveTeamMembers(members: any[]) {
    return await this.request('/site-info/team-members', {
      method: 'PUT',
      body: JSON.stringify({ members }),
    });
  }

  async upsertTeamMember(member: any) {
    return await this.request('/site-info/team-members', {
      method: 'POST',
      body: JSON.stringify(member),
    });
  }

  async updateTeamMember(id: string, member: any) {
    return await this.request(`/site-info/team-members/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(member),
    });
  }

  async deleteTeamMember(id: string) {
    return await this.request(`/site-info/team-members/${encodeURIComponent(id)}`, {
      method: 'DELETE'
    });
  }

  async deleteAllTeamMembers() {
    return await this.request('/site-info/team-members', {
      method: 'DELETE'
    });
  }

  // Content (Blogs + News)
  async getBlogs(params: { status?: string; search?: string; category?: string; page?: number; limit?: number } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/content/blogs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      return await this.request(endpoint);
    } catch {
      const fallback = `/blogs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      return await this.request(fallback);
    }
  }

  async saveBlogs(blogs: any[]) {
    try {
      return await this.request('/content/blogs', {
        method: 'PUT',
        body: JSON.stringify({ blogs }),
      });
    } catch (err) {
      const results: any[] = [];
      for (const b of Array.isArray(blogs) ? blogs : []) {
        const hasBackendId = !!(b && b._id);
        const payload = { ...b };
        if (hasBackendId) {
          const id = String(b._id);
          const updated = await this.request(`/blogs/${encodeURIComponent(id)}`, {
            method: 'PUT',
            body: JSON.stringify(payload),
          });
          results.push(updated);
        } else {
          const created = await this.request('/blogs', {
            method: 'POST',
            body: JSON.stringify(payload),
          });
          results.push(created);
        }
      }
      return { blogs: results } as any;
    }
  }

  async getNews(params: { status?: string; search?: string; page?: number; limit?: number } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/content/news${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await this.request(endpoint);
  }

  async saveNews(news: any[]) {
    return await this.request('/content/news', {
      method: 'PUT',
      body: JSON.stringify({ news }),
    });
  }

  async deleteNewsItem(id: string) {
    return await this.request(`/news/${id}`, { method: 'DELETE' });
  }

  // Site sections (general settings like site_info, trusted_companies)
  async getSectionSettings(section: string) {
    if (section === 'site_info') {
      return await this.getSiteConfig();
    }
    return await this.request(`/site/sections/${section}`);
  }

  async saveSectionSettings(section: string, config: any, published: boolean = true) {
    if (section === 'site_info') {
      return await this.saveSiteConfig(config);
    }
    return await this.request(`/site/sections/${section}`, {
      method: 'PUT',
      body: JSON.stringify({ config, published }),
    });
  }

  // Dedicated Site Config (preferred over section settings)
  async getSiteConfig() {
    return await this.request('/site-info/config');
  }

  async saveSiteConfig(config: any) {
    return await this.request('/site-info/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // Employers
  async getEmployers(params: { page?: number; limit?: number; search?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/employers${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await this.request(endpoint);
  }

  async getEmployerById(id: string) {
    return await this.request(`/employers/${id}`);
  }

  async createEmployer(payload: any) {
    return await this.request('/employers', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateEmployer(id: string, payload: any) {
    return await this.request(`/employers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteEmployer(id: string) {
    return await this.request(`/employers/${id}`, { method: 'DELETE' });
  }

  // Employer self-service profile
  async getEmployerProfile() {
    return await this.request('/employers/profile');
  }

  async updateEmployerProfile(payload: any) {
    return await this.request('/employers/profile', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async uploadEmployerLogo(file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    return await this.request('/employers/upload-logo', {
      method: 'POST',
      body: formData,
    });
  }

  async uploadEmployerBanner(file: File) {
    const formData = new FormData();
    formData.append('banner', file);
    return await this.request('/employers/upload-banner', {
      method: 'POST',
      body: formData,
    });
  }

  async getEmployerSubscription() {
    return await this.request('/employers/subscription');
  }

  async upgradeEmployerSubscription(payload: any) {
    return await this.request('/employers/subscription/upgrade', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Payments & Subscriptions (admin)
  async getPayments(params: { status?: string; page?: number; limit?: number; q?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/admin/payments${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      const res = await this.request(endpoint);
      return Array.isArray((res as any)?.payments) ? (res as any).payments : (Array.isArray(res) ? res : []);
    } catch (err: any) {
      console.warn('getPayments fallback due to error:', err?.message || err);
      // Fallback to empty list until backend is ready
      return [];
    }
  }

  async updatePaymentStatus(id: string, status: 'pending' | 'completed' | 'failed' | 'refunded') {
    try {
      const res = await this.request(`/admin/payments/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      return (res as any)?.payment ?? res;
    } catch (err) {
      console.warn('updatePaymentStatus failed:', err);
      throw err;
    }
  }

  async refundPayment(id: string) {
    try {
      const res = await this.request(`/admin/payments/${id}/refund`, { method: 'POST' });
      return (res as any)?.payment ?? res;
    } catch (err) {
      console.warn('refundPayment failed:', err);
      throw err;
    }
  }

  async getSubscriptions(params: { status?: string; page?: number; limit?: number; q?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/admin/subscriptions${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      const res = await this.request(endpoint);
      return Array.isArray((res as any)?.subscriptions) ? (res as any).subscriptions : (Array.isArray(res) ? res : []);
    } catch (err: any) {
      console.warn('getSubscriptions fallback due to error:', err?.message || err);
      // Fallback: use employer subscription if available, else none
      try {
        const sub = await this.getEmployerSubscription();
        return sub ? [sub] : [];
      } catch {
        return [];
      }
    }
  }

  // Admin Companies
  async getAdminCompanies(params: { page?: number; limit?: number; search?: string; status?: string; verified?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/admin/companies${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      const res = await this.request(endpoint);
      return Array.isArray((res as any)?.companies) ? (res as any).companies : (Array.isArray(res) ? res : []);
    } catch (err: any) {
      console.warn('getAdminCompanies failed:', err?.message || err);
      throw err;
    }
  }

  async createCompanyByAdmin(payload: any) {
    try {
      const res = await this.request('/admin/companies', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('createCompanyByAdmin failed:', err);
      throw err;
    }
  }

  async updateCompanyByAdmin(id: string, payload: any) {
    try {
      const res = await this.request(`/admin/companies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('updateCompanyByAdmin failed:', err);
      throw err;
    }
  }

  async deleteCompanyByAdmin(id: string, options: { force?: boolean } = {}) {
    const query = options.force ? '?force=true' : '';
    try {
      const res = await this.request(`/admin/companies/${id}${query}`, { method: 'DELETE' });
      return res;
    } catch (err) {
      console.warn('deleteCompanyByAdmin failed:', err);
      throw err;
    }
  }

  async approveCompanyByAdmin(id: string) {
    try {
      const res = await this.request(`/admin/companies/${id}/approve`, { method: 'POST' });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('approveCompanyByAdmin failed:', err);
      throw err;
    }
  }

  // Admin: Create employer user + company
  async createEmployerByAdmin(payload: any) {
    try {
      const res = await this.request('/admin/employers', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res as any;
    } catch (err) {
      console.warn('createEmployerByAdmin failed:', err);
      throw err;
    }
  }

  async uploadCompanyLogoByAdmin(id: string, file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await this.request(`/admin/companies/${id}/upload-logo`, {
        method: 'POST',
        body: formData,
      });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('uploadCompanyLogoByAdmin failed:', err);
      throw err;
    }
  }

  async uploadTrustedCompanyLogoByAdmin(id: string, file: File) {
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await this.request(`/admin/trusted-companies/${id}/upload-logo`, {
        method: 'POST',
        body: formData,
      });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('uploadTrustedCompanyLogoByAdmin failed:', err);
      throw err;
    }
  }

  // Admin Trusted Companies
  async getAdminTrustedCompanies(params: { page?: number; limit?: number; search?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) queryParams.append(key, String(value));
    });
    const endpoint = `/admin/trusted-companies${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    try {
      const res = await this.request(endpoint);
      return Array.isArray((res as any)?.companies) ? (res as any).companies : (Array.isArray(res) ? res : []);
    } catch (err: any) {
      console.warn('getAdminTrustedCompanies failed:', err?.message || err);
      throw err;
    }
  }

  async createTrustedCompanyByAdmin(payload: any) {
    try {
      const res = await this.request('/admin/trusted-companies', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('createTrustedCompanyByAdmin failed:', err);
      throw err;
    }
  }

  async updateTrustedCompanyByAdmin(id: string, payload: any) {
    try {
      const res = await this.request(`/admin/trusted-companies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res as any)?.company ?? res;
    } catch (err) {
      console.warn('updateTrustedCompanyByAdmin failed:', err);
      throw err;
    }
  }

  async deleteTrustedCompanyByAdmin(id: string) {
    try {
      const res = await this.request(`/admin/trusted-companies/${id}`, { method: 'DELETE' });
      return res;
    } catch (err) {
      console.warn('deleteTrustedCompanyByAdmin failed:', err);
      throw err;
    }
  }

  // NEW: Reset endpoints
  async resetCompanies(mode: 'all' | 'sample' = 'all') {
    try {
      const res = await this.request(`/admin/companies/reset?mode=${mode}`, { method: 'DELETE' });
      return res;
    } catch (err) {
      console.warn('resetCompanies failed:', err);
      throw err;
    }
  }

  async resetEmployers() {
    try {
      const res = await this.request('/admin/employers/reset', { method: 'DELETE' });
      return res;
    } catch (err) {
      console.warn('resetEmployers failed:', err);
      throw err;
    }
  }
  // Admin Jobs - listing for dashboard
  async getAdminJobs(params: { page?: number; limit?: number; status?: string } = {}) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    const endpoint = `/admin/jobs/all${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const res = await this.request(endpoint);
    const jobs = Array.isArray(res?.jobs)
      ? res.jobs
      : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
    return { jobs, pagination: res?.pagination };
  }

  // Admin dashboard statistics
  async getAdminDashboardStats() {
    // Returns { totalUsers, activeUsers, totalStaff, activeStaff, totalJobs, totalCompanies, totalApplications, recentJobs, recentApplications }
    return await this.request('/admin/dashboard/stats');
  }

  // Admin job statistics
  async getAdminJobStats() {
    // Returns { success, data: { total, pending, approved, rejected, by_tier: { megajob, premium, prime, newspaper } } }
    return await this.request('/admin/jobs/stats');
  }

  // Admin job approval management
  async approveAdminJob(id: string, tier: 'megajob' | 'premium' | 'prime' | 'newspaper', notes?: string) {
    return await this.request(`/admin/jobs/${id}/approve`, {
      method: 'PUT',
      body: JSON.stringify({ tier, notes }),
    });
  }

  async rejectAdminJob(id: string, reason: string) {
    return await this.request(`/admin/jobs/${id}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    });
  }

  async updateAdminJobTier(id: string, tier: 'megajob' | 'premium' | 'prime' | 'newspaper') {
    return await this.request(`/admin/jobs/${id}/tier`, {
      method: 'PUT',
      body: JSON.stringify({ tier }),
    });
  }

  // Upload job cover image (admin)
  async uploadAdminJobCover(id: string, file: File) {
    const formData = new FormData();
    formData.append('cover', file);
    return await this.request(`/admin/jobs/${id}/upload-cover`, {
      method: 'POST',
      body: formData,
    });
  }

  async getCategories() {
    return await this.request('/categories');
  }

  async getCategoryStats(ids?: string[]) {
    const params = new URLSearchParams();
    if (Array.isArray(ids) && ids.length) params.set('ids', ids.join(','));
    const q = params.toString();
    return await this.request(`/categories/stats${q ? `?${q}` : ''}`);
  }

  async createCategory(payload: { name: string; slug: string; icon_url?: string; description?: string; is_scroll?: boolean; scroll_order?: number; }) {
    return await this.request('/categories', { method: 'POST', body: JSON.stringify(payload) });
  }

  async updateCategory(id: string, payload: Partial<{ name: string; slug: string; icon_url: string; description: string; is_scroll: boolean; scroll_order: number; }>) {
    return await this.request(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
  }

  async deleteCategory(id: string) {
    return await this.request(`/categories/${id}`, { method: 'DELETE' });
  }

  async uploadCategoryIcon(id: string, file: File) {
    const form = new FormData();
    form.append('icon', file);
    return await this.request(`/categories/${id}/upload-icon`, { method: 'POST', body: form, headers: {} as any });
  }

  async getScrollCategories() {
    return await this.request('/categories/scroll');
  }

  async setCategoryScroll(id: string, opts: { is_scroll: boolean; scroll_order?: number; }) {
    return await this.request(`/categories/${id}/scroll`, { method: 'PUT', body: JSON.stringify(opts) });
  }
}

export const apiClient = new APIClient();
export default APIClient;
/// <reference types="vite/client" />
